import { motion } from 'framer-motion';
import { Check, X, Minus } from 'lucide-react';
import { Button } from '../../../components/ui';

// Heading + supporting copy by accuracy band. `tone` is one of
// 'strong' | 'solid' | 'uneven' | 'neutral'. Bands match the audit decision:
// ≥85 strong, 60–84 solid, <60 uneven, no answers neutral.
const TONE_COPY = {
  strong:  { heading: 'Strong session.',     sub: 'These are settling in.' },
  solid:   { heading: 'Solid work.',         sub: 'Most of these are sticking; a few need another pass.' },
  uneven:  { heading: 'Worth another pass.', sub: 'A handful didn’t land — they’ll come back sooner.' },
  neutral: { heading: 'Session complete',    sub: null },
};

export default function SessionCompleteSummary({
  wordsCount,
  firstAttemptRate,
  stage3Correct,
  stage3Total,
  stage3Skipped,
  stage3TileResults,
  words,
  tone = 'neutral',
  onDone,
  sessionType = 'onboarding',
}) {
  const hasSortDetail = stage3TileResults && stage3TileResults.length > 0;
  // Review-shape per-card data carries a `format` field; onboarding-shape
  // carries stage_*_*. Use that to pick the right card renderer.
  const isReviewShape = words && words[0] && words[0].format;
  const copy = TONE_COPY[tone] || TONE_COPY.neutral;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="text-center py-8"
    >
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border-2 border-gray-300 dark:border-gray-600 mb-6">
        <Check className="w-8 h-8 text-gray-700 dark:text-gray-300" strokeWidth={1.5} />
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
        {copy.heading}
      </h2>

      <div className="space-y-1 mb-6">
        {copy.sub && (
          <p className="text-sm text-gray-500 dark:text-gray-500 max-w-md mx-auto">
            {copy.sub}
          </p>
        )}
        <p className="text-gray-600 dark:text-gray-400 pt-1">
          <span className="font-semibold text-gray-900 dark:text-white">{wordsCount}</span>{' '}
          {wordsCount === 1 ? 'word' : 'words'}{' '}
          {sessionType === 'onboarding' ? 'onboarded' : 'reviewed'}
        </p>
        {firstAttemptRate != null && (
          <p className="text-sm text-gray-500 dark:text-gray-500">
            {Math.round(firstAttemptRate * 100)}% first-attempt accuracy
          </p>
        )}
        {!stage3Skipped && stage3Total > 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Synonym sort: {stage3Correct}/{stage3Total} correct
          </p>
        )}
      </div>

      {/* Synonym sort breakdown — column layout mirroring the activity.
          Each column shows correctly-placed tiles (GREEN), tiles wrongly
          dropped into this column (RED, with note about their true home),
          and tiles missing from this column (GREY, with note about where
          the student put them). Misplaced tiles thus appear twice — once
          where the student dropped them, once where they should have been
          — which makes both the mistake and the consequence visible. */}
      {hasSortDetail && (
        <SortBreakdownByColumn tiles={stage3TileResults} />
      )}

      {/* Per-word recap */}
      {words && words.length > 0 && (
        <div className="space-y-3 mb-8 text-left max-w-2xl mx-auto">
          {words.map((w, i) =>
            isReviewShape
              ? <ReviewWordCard key={w.card_id || i} word={w} />
              : <WordCard key={w.word_id || i} word={w} />
          )}
        </div>
      )}

      <Button onClick={onDone}>Done</Button>
    </motion.div>
  );
}

// Sort breakdown rendered as columns, mirroring the activity layout. Each
// column derives from a unique correct_lemma (the original column words),
// plus a final "Neither" column for tiles whose correct placement was no
// group. For every column we render three tile buckets in order:
//   - correct: assigned == column && correct == column          → GREEN ✓
//   - misplaced here: assigned == column && correct != column    → RED ✗
//   - missing: correct == column && assigned != column           → GREY ⊘
// A misplaced tile therefore appears twice: once in red where the student
// dropped it, once in grey under its real home column.
function SortBreakdownByColumn({ tiles }) {
  const columnNames = Array.from(
    new Set(tiles.map((t) => t.correct_lemma).filter(Boolean))
  );

  const buildColumn = (colName) => {
    const correct = tiles.filter(
      (t) => t.assigned_lemma === colName && t.correct_lemma === colName
    );
    const misplacedHere = tiles.filter(
      (t) => t.assigned_lemma === colName && t.correct_lemma !== colName
    );
    const missing = tiles.filter(
      (t) => t.correct_lemma === colName && t.assigned_lemma !== colName
    );
    return { correct, misplacedHere, missing };
  };

  const neitherCorrect = tiles.filter(
    (t) => !t.assigned_lemma && !t.correct_lemma
  );
  const neitherMisplaced = tiles.filter(
    (t) => !t.assigned_lemma && t.correct_lemma
  );

  const showNeither = neitherCorrect.length > 0 || neitherMisplaced.length > 0;

  return (
    <div className="mb-8 max-w-3xl mx-auto text-left">
      <p className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3 px-1">
        Sort breakdown
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {columnNames.map((colName) => (
          <SortColumn key={colName} title={colName} {...buildColumn(colName)} />
        ))}
        {showNeither && (
          <SortColumn
            title="Neither"
            italicTitle
            correct={neitherCorrect}
            misplacedHere={neitherMisplaced}
            missing={[]}
          />
        )}
      </div>
    </div>
  );
}

function SortColumn({ title, italicTitle = false, correct, misplacedHere, missing }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 min-h-[6rem]">
      <h4
        className={[
          'text-sm font-semibold text-gray-900 dark:text-white mb-2 border-b border-gray-100 dark:border-gray-800 pb-1.5',
          italicTitle ? 'italic text-gray-500 dark:text-gray-400 font-medium' : '',
        ].join(' ')}
      >
        {title}
      </h4>
      <ul className="space-y-1.5">
        {correct.map((t) => (
          <SortTile
            key={`ok-${t.lemma}`}
            tone="good"
            lemma={t.lemma}
          />
        ))}
        {misplacedHere.map((t) => (
          <SortTile
            key={`wrong-${t.lemma}`}
            tone="bad"
            lemma={t.lemma}
            note={
              t.correct_lemma
                ? `belongs in ${t.correct_lemma}`
                : 'belongs in no group'
            }
          />
        ))}
        {missing.map((t) => (
          <SortTile
            key={`miss-${t.lemma}`}
            tone="missing"
            lemma={t.lemma}
            note={
              t.assigned_lemma
                ? `you put it in ${t.assigned_lemma}`
                : 'you left this out'
            }
          />
        ))}
        {correct.length === 0 && misplacedHere.length === 0 && missing.length === 0 && (
          <li className="text-xs text-gray-300 dark:text-gray-600 italic">empty</li>
        )}
      </ul>
    </div>
  );
}

function SortTile({ tone, lemma, note }) {
  const styles = {
    good: {
      Icon: Check,
      iconColor: 'text-green-600 dark:text-green-400',
      lemmaColor: 'text-gray-900 dark:text-white',
    },
    bad: {
      Icon: X,
      iconColor: 'text-red-500 dark:text-red-400',
      lemmaColor: 'text-red-700 dark:text-red-300 line-through',
    },
    missing: {
      Icon: Minus,
      iconColor: 'text-gray-400 dark:text-gray-500',
      lemmaColor: 'text-gray-400 dark:text-gray-500',
    },
  }[tone];

  const { Icon, iconColor, lemmaColor } = styles;

  return (
    <li className="text-sm flex items-start gap-1.5">
      <Icon
        className={`w-3.5 h-3.5 ${iconColor} flex-shrink-0 mt-0.5`}
        strokeWidth={2.5}
      />
      <div className="flex-1 min-w-0">
        <div className={`font-serif font-medium ${lemmaColor} truncate`}>{lemma}</div>
        {note && (
          <div className="text-[11px] text-gray-400 dark:text-gray-500 leading-tight">
            {note}
          </div>
        )}
      </div>
    </li>
  );
}

// Per-card recap row for the review flow. Compact: lemma, format pill, outcome
// icon, optional expand for the student's response + correct answer + definition.
function ReviewWordCard({ word }) {
  const formatLabel =
    word.format === 'mcq_def_to_word' ? 'Recognize'
      : word.format === 'cloze' ? 'Recall'
      : word.format === 'sentence_writing' ? 'Produce'
      : word.format;

  const ratingTier =
    word.rating === 3 || word.rating === 4 ? 'good'
      : word.rating === 2 ? 'partial'
      : 'bad';

  const Icon = ratingTier === 'good' ? Check : ratingTier === 'partial' ? Minus : X;
  const iconColor =
    ratingTier === 'good' ? 'text-green-600 dark:text-green-400'
      : ratingTier === 'partial' ? 'text-amber-500 dark:text-amber-400'
      : 'text-red-500 dark:text-red-400';

  const responseDiffers =
    word.response &&
    word.correct_answer &&
    word.response.toString().trim().toLowerCase() !==
      word.correct_answer.toString().trim().toLowerCase();
  const hasDetails = word.definition || word.response || responseDiffers;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-3 min-w-0">
          <h4 className="font-serif font-semibold text-gray-900 dark:text-white text-lg truncate">
            {word.lemma}
          </h4>
          <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
            {formatLabel}
          </span>
          {word.attempts > 1 && (
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              · 2nd try
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className={`flex items-center justify-center w-5 h-5 ${iconColor}`}>
            <Icon className="w-4 h-4" strokeWidth={2.5} />
          </div>
        </div>
      </div>

      {hasDetails && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 space-y-2 text-sm">
          {/* Cloze miss-in-context — render the masked sentence twice when
              the student got it wrong: once with their answer in red, once
              with the correct answer in green. Helps the student see how
              their wrong word didn't fit the syntax/semantics of the slot. */}
          {word.format === 'cloze' && word.sentence_with_blank && responseDiffers ? (
            <ClozeMissInContext word={word} />
          ) : (
            <>
              {word.response && (
                <p className="text-gray-600 dark:text-gray-400">
                  You {word.format === 'mcq_def_to_word' ? 'picked' : 'wrote'}:{' '}
                  <span className="font-medium">{word.response}</span>
                </p>
              )}
              {responseDiffers && word.format !== 'sentence_writing' && (
                <p className="text-gray-500 dark:text-gray-500">
                  Correct:{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {word.correct_answer}
                  </span>
                </p>
              )}
            </>
          )}
          {word.definition && (
            <p className="text-gray-500 dark:text-gray-500 italic">
              {word.definition}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function WordCard({ word }) {
  const hasDetails = word.stage_4_response || word.stage_4_correct_answer;

  // Map cloze rating ("good"/"hard"/"again") into the StagePill tier vocabulary.
  const stage4Tier =
    word.stage_4_rating === 'good'
      ? 'yes'
      : word.stage_4_rating === 'hard'
      ? 'sort_of'
      : word.stage_4_rating === 'again'
      ? 'way_off'
      : null;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between gap-3">
        <h4 className="font-serif font-semibold text-gray-900 dark:text-white text-lg">
          {word.lemma}
        </h4>
        <div className="flex items-center gap-3">
          <StagePill label="Context" tier={word.stage_1_tier} />
          <StagePill label="Recognition" boolCorrect={word.stage_2_5_correct} />
          <StagePill label="Recall" tier={stage4Tier} />
        </div>
      </div>

      {hasDetails && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 space-y-1.5 text-sm">
          {word.stage_4_response && (
            <p className="text-gray-600 dark:text-gray-400">
              You typed: <span className="font-medium">{word.stage_4_response}</span>
            </p>
          )}
          {word.stage_4_correct_answer &&
            word.stage_4_correct_answer.toLowerCase() !==
              (word.stage_4_response || '').trim().toLowerCase() && (
              <p className="text-gray-500 dark:text-gray-500">
                Correct form: <span className="font-medium text-gray-700 dark:text-gray-300">{word.stage_4_correct_answer}</span>
              </p>
            )}
        </div>
      )}
    </div>
  );
}

function StagePill({ label, tier, boolCorrect }) {
  // Resolve a status from either tier (yes/sort_of/way_off) or a boolean.
  let status;
  if (tier === 'yes') status = 'good';
  else if (tier === 'sort_of') status = 'partial';
  else if (tier === 'way_off') status = 'bad';
  else if (boolCorrect === true) status = 'good';
  else if (boolCorrect === false) status = 'bad';
  else status = 'skipped';

  const Icon =
    status === 'good' ? Check : status === 'bad' ? X : status === 'partial' ? Minus : null;

  const colorClass =
    status === 'good'
      ? 'text-green-600 dark:text-green-400'
      : status === 'bad'
      ? 'text-red-500 dark:text-red-400'
      : status === 'partial'
      ? 'text-amber-500 dark:text-amber-400'
      : 'text-gray-300 dark:text-gray-600';

  return (
    <div className="flex flex-col items-center" title={label}>
      <div className={`flex items-center justify-center w-5 h-5 ${colorClass}`}>
        {Icon ? <Icon className="w-4 h-4" strokeWidth={2.5} /> : <Minus className="w-3 h-3" />}
      </div>
      <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{label}</span>
    </div>
  );
}

// Renders the cloze's masked sentence twice — once with the student's
// (wrong) answer in red, once with the correct answer in emerald — so
// the student can see how their guess didn't fit the slot.
function ClozeMissInContext({ word }) {
  const sentence = word.sentence_with_blank || '';
  const parts = sentence.split('___');
  // No blank found in the stored sentence — fall back to the simple
  // "You wrote / Correct" layout.
  if (parts.length < 2) {
    return (
      <>
        <p className="text-gray-600 dark:text-gray-400">
          You wrote: <span className="font-medium">{word.response}</span>
        </p>
        <p className="text-gray-500 dark:text-gray-500">
          Correct:{' '}
          <span className="font-medium text-gray-700 dark:text-gray-300">
            {word.correct_answer}
          </span>
        </p>
      </>
    );
  }
  const before = parts[0];
  const after = parts.slice(1).join('___');
  return (
    <div className="space-y-1.5 font-serif text-base leading-relaxed">
      <p className="text-gray-700 dark:text-gray-300">
        <span className="text-gray-400 dark:text-gray-500 not-italic font-sans text-xs uppercase tracking-wide mr-2">You</span>
        {before}
        <span className="font-semibold text-rose-600 dark:text-rose-400 underline decoration-rose-300 dark:decoration-rose-700 underline-offset-2">
          {word.response}
        </span>
        {after}
      </p>
      <p className="text-gray-700 dark:text-gray-300">
        <span className="text-gray-400 dark:text-gray-500 not-italic font-sans text-xs uppercase tracking-wide mr-2">Correct</span>
        {before}
        <span className="font-semibold text-emerald-700 dark:text-emerald-400">
          {word.correct_answer}
        </span>
        {after}
      </p>
    </div>
  );
}
